import styled, { css } from 'styled-components';
import React, { FC, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { ImageDataLike } from 'gatsby-plugin-image';
import { useModal } from '../context/modalContext';
import { Button, ButtonProps } from './Button';
import { Typography } from './Typography';
import { ArrowRight, CloseModal } from './icons';
import { Spacer } from './Spacer';
import { Divider } from './Divider';
// import { Image } from './Image';
import { useDataLayerPush } from '../hooks/useDataLayerPush';
import tokens from '../tokens/tokens';

interface PopUpProps {
  modalState?: 'open' | 'closed';
  isButtonBarFormPopup?: boolean;
  heading?: string;
  subHeading?: string;
  actionLabel?: string;
  hideClose?: boolean;
  secondaryButton?: ButtonProps;
  secondaryCloseButton?: ButtonProps;
  primaryButton?: ButtonProps;
  actionVariant?:
    | 'secondary'
    | 'primary'
    | 'secondaryOutline'
    | 'whiteOutline'
    | 'secondaryInverseOutline';
  actionIcon?: any;
  actionIconLarge?: boolean;
  gaData?: Record<string, any>;
  videoThumbnail?: boolean;
  image?: ImageDataLike | string;
  modalButtonLabel?: string;
  customAction?: React.JSX.Element;
  narrow?: boolean;
  isMaxExceeded?: boolean;
  hasAnyButton?: boolean;
  largeButton?: boolean;
  onValidateBeforeClose?: () => boolean;
  onClose?: () => void;
}

interface StyledHeaderProps {
  heading?: string;
}

interface StyledSubHeaderProps {
  isMaxExceeded?: boolean;
}

interface StyledBodyProps {
  hasAnyButton?: boolean;
}

const StyledModal = styled.div<PopUpProps>`
  ${({ theme: { space, colors, borderRadii, maxMedia }, narrow }) => css`
    margin: 32px;
    width: 100%;
    ${narrow ? `max-width: 606px;` : `max-width: 808px;`}
    max-height:calc(100dvh - 32px);
    display: flex;
    flex-direction: column;
    background-color: #fff;
    border-radius: 4px;
    padding: 0 32px 0 0;

    @media (max-width: ${maxMedia.md}) {
      padding: 0 24px 0 0;
    }
  `};
`;

const StyledHeader = styled.div<StyledHeaderProps>`
  ${({ theme: { space, colors, maxMedia }, heading }) => css`
    background-color: #fff;
    padding: ${heading
      ? `64px 0 48px 64px`
      : `32px 0 16px 0`};
    display: flex;
    flex-direction: column-reverse;
    gap: 16px;
    align-items: flex-start;
    justify-content: ${heading ? 'space-between' : 'flex-end'};
    position: relative;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;

    @media (max-width: ${maxMedia.md}) {
      padding: 24px 0 24px 24px;
    }
  `};
`;

const StyledCloseCta = styled(Button)`
    align-items: center;
    justify-content: center;
    align-self: flex-end;
    display: flex;
    color: #fff;
    padding: 0 !important;
    border: none;
    border-radius: 0;
    width: 40px;
    height: 40px;

    svg {
      width: 32px;
      height: 32px;
    }
`;

const StyledBody = styled.div<StyledBodyProps>`
  ${({ theme: { space, colors, borderRadii, maxMedia }, hasAnyButton }) => css`
    padding: 0 64px ${hasAnyButton ? '0' : '64px'} 64px;
    background-color: #fff;
    overflow-y: auto;
    border-bottom-left-radius: ${hasAnyButton
      ? '0'
      : borderRadii[tokens.popupBorderRadius]};
    border-bottom-right-radius: ${hasAnyButton
      ? '0'
      : borderRadii[tokens.popupBorderRadius]};
    flex: 1;

    @media (min-height: 870px) {
      min-height: 340px;
    }

    ${tokens.styleScrollbars &&
    css`
      /* Scrollbar styling */
      &::-webkit-scrollbar {
        width: 8px;
      }

      &::-webkit-scrollbar-track {
        background: #f1f1f1;
      }

      &::-webkit-scrollbar-thumb {
        background-color: ${colors.brandTwo100};
        border-radius: 4px;
      }
    `}

    @media (max-width: ${maxMedia.md}) {
      padding: 0 24px;
    }
  `};
`;

const StyledButton = styled.button`
  ${({ theme: { borderRadii, colors } }) => css`
    position: relative;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: ${colors.brandOne100};
      opacity: 0.5;
      z-index: 1;
      border-radius: ${borderRadii.medium};
    }
    svg {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 3;
    }
  `};
`;

const StyledCustomAction = styled.button`
  ${({ theme: { colors } }) => css`
    position: relative;
    width: 100%;
  `};
`;

const StyledFooter = styled.div`
  ${({ theme: { maxMedia, colors } }) => css`
    padding: 24px 0 32px 32px;
    background-color: #fff;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;

    @media (max-width: ${maxMedia.md}) {
      padding: 24px 0 24px 24px;
    }
  `};
`;

const StyledFooterInner = styled.div`
  ${({ theme: { space, maxMedia } }) => css`
    display: flex;
    gap: 32px;
    flex-wrap: wrap;

    // make buttons full width on mobile

    @media (max-width: ${maxMedia.md}) {
      flex-direction: column;
      gap: 32px;
    }
  `};
`;

const StyledSpacer = styled(Spacer)`
  width: 100%;
`;

const StyledModalOverlay = styled.div`
  ${({ theme: { colors, layers } }) => css`
    position: fixed;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999999;
    background: #02315B;
    background-color: rgba(${tokens.overlayColor});
  `};
`;
const PlayIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" fill="none">
    <path
      fill="#fff"
      fillRule="evenodd"
      d="M20 64c0 24.26 19.74 44 44 44s44-19.74 44-44-19.74-44-44-44-44 19.74-44 44Zm-8 0c0-28.672 23.328-52 52-52s52 23.328 52 52-23.328 52-52 52-52-23.328-52-52Zm34-28v57.1l45.24-28.54L46 36Zm8.2 42.24V50.88l21.68 13.68L54.2 78.24Z"
      clipRule="evenodd"
    />
  </svg>
);

// Put the modal at the end of the root Gatsby app element. If that doesn't exist, like in Storybook, fallback to the body
Modal.setAppElement('#___gatsby, body');

export const PopUp: FC<React.PropsWithChildren<PopUpProps>> = ({
  videoThumbnail,
  image,
  heading,
  subHeading,
  actionIcon,
  actionIconLarge,
  actionLabel,
  actionVariant = 'secondary',
  children,
  hideClose,
  secondaryButton,
  secondaryCloseButton,
  primaryButton,
  gaData,
  isButtonBarFormPopup,
  modalButtonLabel,
  customAction,
  narrow,
  modalState,
  largeButton,
  onValidateBeforeClose,
  ...rest
}) => {
  const { toggleModal: toggleContextModal = () => {} } = useModal() || {};
  const [isOpen, setIsOpen] = useState(false);
  const { dataLayerPush } = useDataLayerPush();
  const hasAnyButton =
    !!secondaryButton || !!primaryButton || !!secondaryCloseButton;

  const toggleModal = (forceClose = false) => {
    if (isOpen && onValidateBeforeClose && !forceClose) {
      const isValid = onValidateBeforeClose();
      if (!isValid) {
        return; // Prevent modal from closing
      }
    }

    setIsOpen(!isOpen);
    if (!isOpen) {
      toggleContextModal(); // Open modal
    } else {
      toggleContextModal(); // Close modal
      if (modalState === 'open') {
        // Notify parent to close
        rest.onClose?.(); // Optional callback to sync parent state
      }
    }

    if (!isOpen && isButtonBarFormPopup) {
      dataLayerPush({
        content_group: 'brochureware pages',
        event: 'click form modal',
        modal_class: 'modal opened',
        modal_text: modalButtonLabel,
        modal_id: '',
        modal_url: '#form',
      });
    }

    if (isOpen && isButtonBarFormPopup) {
      dataLayerPush({
        content_group: 'brochureware pages',
        event: 'click form modal',
        modal_class: 'modal closed',
        modal_text: 'Close X',
        modal_id: 'Close X',
        modal_url: '#close',
      });
    }
  };

  // Secondary button clicks

  const handleSecondaryButtonClick = () => {
    if (secondaryButton?.onClick) {
      secondaryButton?.onClick();
    }
    toggleModal();
    // toggleContextModal();
  };

  // Primary button clicks

  const handlePrimaryButtonClick = () => {
    if (primaryButton?.onClick) {
      primaryButton?.onClick();
    }
    toggleModal();
    // toggleContextModal();
  };

  // Manually set modal state
  useEffect(() => {
    if (modalState === 'open') {
      setIsOpen(true);
    } else if (modalState === 'closed') {
      setIsOpen(false);
    }
  }, [modalState]);

  // Map PopUp actionVariant to Button variant (only 'solid' and 'outline' exist)
  const getButtonVariant = (variant: string) => {
    switch (variant) {
      case 'primary':
      case 'secondary':
        return 'solid';
      case 'secondaryOutline':
      case 'whiteOutline':
      case 'secondaryInverseOutline':
        return 'outline';
      default:
        return 'solid';
    }
  };

  const generateTrigger = () => {
    // if (videoThumbnail) {
    //   return (
    //     <StyledButton
    //       onClick={() => {
    //         toggleModal();
    //       }}
    //     >
    //       {image && (
    //         <Image
    //           src={image}
    //           alt="Thumbnail"
    //           loading="eager"
    //           roundCorners="all"
    //         />
    //       )}
    //       <PlayIcon aria-label="Play Video" />
    //     </StyledButton>
    //   );
    // }

    if (customAction) {
      return (
        <StyledCustomAction
          type="button"
          onClick={() => {
            toggleModal();
          }}
        >
          {customAction}
        </StyledCustomAction>
      );
    }

    

    return (
      <Button
        variant={actionVariant}
        data-test="popup-action"
        type="button"
        aria-haspopup="dialog"
        onClick={() => {
          toggleModal();
        }}
        iconLeft={actionIcon}
        small={!largeButton}
        {...rest}
      >
        {actionLabel}
      </Button>
    );
  };

  
  return (
    <>
      {generateTrigger()}
      <Modal
        isOpen={isOpen}
        onRequestClose={(e) => {
          // Check if the event source is the close button
          const isCloseButton =
            (e?.target as HTMLElement)?.getAttribute('data-close-btn') ===
            'true';

          if (
            !isCloseButton &&
            onValidateBeforeClose &&
            !onValidateBeforeClose()
          ) {
            return; // Prevent modal from closing
          }
          toggleModal(true); // Force close
        }}
        bodyOpenClassName="prevent-body-scroll"
        className="_"
        overlayClassName="_"
        contentElement={(props, children) => (
          <StyledModal narrow={narrow} {...props}>
            {children}
          </StyledModal>
        )}
        overlayElement={(props, contentElement) => (
          <StyledModalOverlay {...props}>{contentElement}</StyledModalOverlay>
        )}
        shouldCloseOnEsc
        shouldCloseOnOverlayClick
        data-test="popup-modal"
        aria={{
          label: heading || 'Popup',
        }}
      >
        <StyledHeader heading={heading}>
          {heading && (
            <Spacer gap="fluidXLarge">
              <Spacer gap="fluidXSmall">
                {heading && (
                  <Typography component="h3" variant="h3">
                    {heading}
                  </Typography>
                )}
                {subHeading && (
                  <Typography component="p" variant="paragraph">
                    {subHeading}
                  </Typography>
                )}
              </Spacer>
            </Spacer>
          )}
          {!hideClose && (
            <StyledCloseCta
              iconRight={<CloseModal />}
              variant="solidPrimary"
              type="button"
              a11yTitle="Close Popup"
              onClick={() => toggleModal(true)}
              data-close-btn="true"
            />
          )}
        </StyledHeader>
        <StyledBody data-test="popup-modal-body" hasAnyButton={hasAnyButton}>
          {children}
        </StyledBody>
        {(secondaryButton || primaryButton || secondaryCloseButton) && (
          <StyledFooter>
            <StyledSpacer gap="xLarge">
              {tokens.includeFooterDivider && <Divider />}
              <StyledFooterInner>
                {primaryButton && (
                  <Button
                    type="button"
                    label={primaryButton.label}
                    onClick={() => handlePrimaryButtonClick()}
                    data-test="popup-modal-action-primary"
                    iconRight={
                      primaryButton.iconRight === undefined ? (
                        <ArrowRight />
                      ) : (
                        primaryButton.iconRight
                      )
                    }
                    variant={getButtonVariant(primaryButton.variant || 'primary')}
                  />
                )}
                {secondaryButton && (
                  <Button
                    type="button"
                    variant="outline"
                    label={secondaryButton.label}
                    onClick={() => handleSecondaryButtonClick()}
                    data-test="popup-modal-action-secondary"
                  />
                )}
                {secondaryCloseButton && (
                  <Button
                    variant="solid"
                    type="button"
                    label={secondaryCloseButton.label}
                    onClick={() => {
                      toggleModal();
                    }}
                    data-test="popup-modal-action-secondary-close"
                  />
                )}
              </StyledFooterInner>
            </StyledSpacer>
          </StyledFooter>
        )}
      </Modal>
    </>
  );
};

export default PopUp;
